'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { SAAS_SIGNUP_URL, SAAS_LOGIN_URL, SAAS_DASHBOARD_URL, SAAS_SUBSCRIPTION_URL } from '@/lib/saas';

const navLinks = [
  { label: 'Início', href: '/' },
  { label: 'Funcionalidades', href: '/features' },
  { label: 'Planos', href: '/pricing' },

  { label: 'Contato', href: '/contact' },
];

interface SaasUser {
  name: string;
  email: string;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState<SaasUser | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const handleScroll = () => setMobileOpen(false);
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [mobileOpen]);

  // Detect authenticated user via postMessage from SaaS iframe or localStorage token
  useEffect(() => {
    try {
      const stored = localStorage.getItem('nf_user');
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch {
      // no stored user
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://app.usenegociofacil.com.br') return;
      if (event.data?.type === 'NF_AUTH' && event.data?.user) {
        setUser(event.data.user);
        try { localStorage.setItem('nf_user', JSON.stringify(event.data.user)); } catch {}
      }
      if (event.data?.type === 'NF_LOGOUT') {
        setUser(null);
        try { localStorage.removeItem('nf_user'); } catch {}
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleLogout = () => {
    setUser(null);
    setUserMenuOpen(false);
    try { localStorage.removeItem('nf_user'); } catch {}
    window.open(`${SAAS_LOGIN_URL}?logout=true`, '_blank');
  };

  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background/90 backdrop-blur-xl border-b border-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between">
        {/* Logo → Home */}
        <Link href="/" className="flex items-center gap-2 group">
          <AppLogo size={36} onClick={() => {}} />
          <span className="font-extrabold text-lg tracking-tight text-foreground group-hover:text-primary transition-colors">
            NegócioFácil
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* CTA / User Menu */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
                  {firstName.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-semibold text-foreground">Olá, {firstName}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`text-muted-foreground transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}>
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 glass-card rounded-xl border border-border shadow-xl overflow-hidden z-50">
                  <a
                    href={SAAS_DASHBOARD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground hover:bg-primary/10 transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                    </svg>
                    Ir para o Sistema
                  </a>
                  <a
                    href={SAAS_SUBSCRIPTION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground hover:bg-primary/10 transition-colors border-t border-border/50"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    </svg>
                    Minha Assinatura
                  </a>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-400/10 transition-colors border-t border-border/50"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Sair
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <a
                href={SAAS_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                Entrar
              </a>
              <a
                href={`${SAAS_SIGNUP_URL}?trial=14&utm_source=header`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2"
              >
                Testar Grátis — 14 dias
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                </svg>
              </a>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2 group"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          <span className={`block h-0.5 bg-foreground transition-all duration-300 ${mobileOpen ? 'w-6 rotate-45 translate-y-2' : 'w-6'}`} />
          <span className={`block h-0.5 bg-foreground transition-all duration-300 ${mobileOpen ? 'opacity-0 w-0' : 'w-4'}`} />
          <span className={`block h-0.5 bg-foreground transition-all duration-300 ${mobileOpen ? 'w-6 -rotate-45 -translate-y-2' : 'w-5'}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border px-4 py-6 flex flex-col gap-4">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className="text-base font-medium text-muted-foreground hover:text-primary transition-colors py-2"
              onClick={() => setMobileOpen(false)}
            >
              {link?.label}
            </Link>
          ))}
          <div className="flex flex-col gap-3 pt-4 border-t border-border">
            {user ? (
              <>
                <p className="text-sm font-semibold text-foreground px-1">Olá, {firstName} 👋</p>
                <a
                  href={SAAS_DASHBOARD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary px-5 py-3 text-sm text-center"
                  onClick={() => setMobileOpen(false)}
                >
                  Ir para o Sistema
                </a>
                <a
                  href={SAAS_SUBSCRIPTION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary-outline px-5 py-3 text-sm text-center"
                  onClick={() => setMobileOpen(false)}
                >
                  Minha Assinatura
                </a>
                <button
                  onClick={handleLogout}
                  className="text-center text-sm font-semibold text-red-400 hover:text-red-300 transition-colors py-2"
                >
                  Sair
                </button>
              </>
            ) : (
              <>
                <a
                  href={SAAS_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors py-2"
                  onClick={() => setMobileOpen(false)}
                >
                  Entrar
                </a>
                <a
                  href={`${SAAS_SIGNUP_URL}?trial=14&utm_source=mobile-header`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary px-5 py-3 text-sm text-center"
                  onClick={() => setMobileOpen(false)}
                >
                  Testar Gratuitamente — 14 dias
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}